import { useState } from 'react';
import { addDays, addMonths, formatDay, formatMonth, isIsoDate, isoWeekday, monthOf, todayIn } from '@tiffinify/core';
import { clock12 } from '../lib/format';
import { Icon } from './ui';
import { Btn, Btn2, Sheet, SheetHead } from './p';

type Props = {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  mode?: 'date' | 'time';
  min?: string;
  max?: string;
  today?: string;
  disabledFor?: (day: string) => boolean;
  clearable?: boolean;
  error?: string;
};

export function ScheduleField({ id, label, value, onChange, mode = 'date', min, max, today: serverToday, disabledFor, clearable, error }: Props) {
  const today = serverToday && isIsoDate(serverToday) ? serverToday : todayIn();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState('');
  const [month, setMonth] = useState('');
  const [time, setTime] = useState('09:00');
  const hasDate = mode === 'date';
  const hasTime = mode === 'time';
  const allowed = (day: string) => (!min || day >= min) && (!max || day <= max) && !disabledFor?.(day);
  const show = () => {
    const currentDate = value.slice(0, 10);
    const initialDate = isIsoDate(currentDate) ? currentDate : min && min > today ? min : today;
    setDate(initialDate);
    setMonth(monthOf(initialDate));
    setTime(mode === 'time' ? value || '09:00' : '09:00');
    setOpen(true);
  };
  const text = value
    ? hasDate ? formatDay(value) : clock12(value)
    : hasDate ? 'Choose a date' : 'Choose a time';
  const hour = Number(time.slice(0, 2));
  const minute = time.slice(3, 5);
  const setClock = (nextHour: number, nextMinute: string) => setTime(String(nextHour).padStart(2, '0') + ':' + nextMinute);
  const first = month ? month + '-01' : today.slice(0, 7) + '-01';
  const dates = Array.from({ length: 42 }, (_, index) => addDays(first, index - isoWeekday(first) + 1));
  return (
    <div className="p-field">
      <label htmlFor={id}>{label}</label>
      <button id={id} type="button" className="p-schedule-value" aria-haspopup="dialog" aria-expanded={open} aria-invalid={error ? true : undefined} aria-describedby={error ? id + '-error' : undefined} onClick={show}>
        <span className={value ? '' : 'p-placeholder'}>{text}</span>
        <Icon name={mode === 'time' ? 'clock' : 'calendar'} size={18} />
      </button>
      {error && <small className="p-err" id={id + '-error'} role="alert">{error}</small>}
      {open && <Sheet label={label} onClose={() => setOpen(false)}>
        <SheetHead title={label} onClose={() => setOpen(false)} />
        {hasDate && <>
          <div className="p-schedule-month">
            <button type="button" className="p-ib" aria-label="Previous month" disabled={!!min && addMonths(month, -1) < monthOf(min)} onClick={() => setMonth(addMonths(month, -1))}><Icon name="left" size={18} /></button>
            <h2>{formatMonth(month)}</h2>
            <button type="button" className="p-ib" aria-label="Next month" disabled={!!max && addMonths(month, 1) > monthOf(max)} onClick={() => setMonth(addMonths(month, 1))}><Icon name="right" size={18} /></button>
          </div>
          <div className="p-schedule-calendar" role="group" aria-label="Choose a date">
            {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => <span key={index} className="p-schedule-weekday" aria-hidden="true">{day}</span>)}
            {dates.map(day => <button key={day} type="button" aria-label={formatDay(day)} aria-pressed={day === date}
              className={[day === date ? 'selected' : '', day === today ? 'today' : '', monthOf(day) !== month ? 'outside' : ''].join(' ')}
              disabled={!allowed(day)} onClick={() => setDate(day)}>{Number(day.slice(8))}</button>)}
          </div>
        </>}
        {hasTime && <div className="p-schedule-clock" role="group" aria-label="Choose a time">
          <div className="p-field"><label htmlFor={id + '-hour'}>Hour</label>
            <select id={id + '-hour'} value={hour % 12 || 12} onChange={event => setClock(Number(event.target.value) % 12 + (hour >= 12 ? 12 : 0), minute)}>
              {Array.from({ length: 12 }, (_, index) => <option key={index + 1} value={index + 1}>{index + 1}</option>)}
            </select>
          </div>
          <div className="p-field"><label htmlFor={id + '-minute'}>Minute</label>
            <select id={id + '-minute'} value={minute} onChange={event => setClock(hour, event.target.value)}>
              {Array.from({ length: 60 }, (_, index) => <option key={index} value={String(index).padStart(2, '0')}>{String(index).padStart(2, '0')}</option>)}
            </select>
          </div>
          <div className="p-field"><label htmlFor={id + '-period'}>AM or PM</label>
            <select id={id + '-period'} value={hour >= 12 ? 'PM' : 'AM'} onChange={event => setClock(hour % 12 + (event.target.value === 'PM' ? 12 : 0), minute)}>
              <option>AM</option><option>PM</option>
            </select>
          </div>
        </div>}
        <Btn label={hasDate && !allowed(date) ? 'Choose an available day' : hasDate ? 'Use ' + formatDay(date) : 'Use ' + clock12(time)}
          disabled={hasDate && !allowed(date)} go="check"
          onClick={() => { onChange(mode === 'time' ? time : date); setOpen(false); }} />
        {clearable && value && <Btn2 label="Clear" onClick={() => { onChange(''); setOpen(false); }} />}
      </Sheet>}
    </div>
  );
}
